import React, { useState, useEffect } from 'react';
import { X, FileIcon, ImageIcon } from 'lucide-react';

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove }) => {
  const isImage = file.type.startsWith('image/');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div className="px-3 pt-2 flex items-center gap-3">
      <div className="relative shrink-0">
        {isImage && previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="w-12 h-12 rounded-lg object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
            {isImage ? <ImageIcon size={20} className="text-gray-400" /> : <FileIcon size={20} className="text-gray-400" />}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
        <p className="text-[10px] text-gray-400">{formatSize(file.size)}</p>
      </div>
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Remove file"
      >
        <X size={16} />
      </button>
    </div>
  );
};
